import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { IAccordionItem, IVariantClasses } from '@models/IAccordionItem';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { PropsWithChildren, useEffect, useState } from 'react';

const AccordionItem = ({
  animateChildren = false,
  buttonClasses,
  callback,
  children,
  dataTestId,
  description,
  isOpen = false,
  padding,
  text,
  title,
  titleEl,
  variant = 'primary',
  filterCount,
}: PropsWithChildren<IAccordionItem>) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  useEffect(() => {
    setIsAccordionOpen(isOpen);
  }, [isOpen]);

  const baseAccordionButtonClasses =
    'tw-w-full tw-flex tw-justify-between tw-items-center tw-text-left tw-gap-4 tw-outline-black tw-overflow-hidden';
  const baseButtonClasses =
    'tw-border-grey-20 hover:tw-text-grey-70 tw-text-black tw-group/button';

  const variantClasses: IVariantClasses = {
    primary: {
      buttonClasses: classNames(baseButtonClasses, 'tw-py-3'),
      buttonHeaderTextStyle: 'heading-21-light',
      buttonContentTextStyle: 'body',
      textContentClasses: 'tw-pt-4 tw-pb-6 tw-bg-white',
    },
    secondary: {
      buttonClasses: classNames(baseButtonClasses, 'py-2', {
        'tw-border-b': !isAccordionOpen,
      }),
      buttonHeaderTextStyle: 'body-14-semibold',
      buttonContentTextStyle: 'body-small',
      textContentClasses: 'tw-pt-4 tw-pb-6 tw-bg-white',
    },
    filter: {
      buttonClasses: classNames(
        baseButtonClasses,
        'tw-p-4 tw-bg-grey-10 tw-border-b'
      ),
      buttonHeaderTextStyle: 'body',
      buttonContentTextStyle: 'body-small',
      textContentClasses: 'tw-border tw-border-t-0',
    },
    listingFilter: {
      buttonClasses: classNames(baseButtonClasses, 'tw-px-xs tw-py-sm'),
      buttonHeaderTextStyle: 'body',
      buttonContentTextStyle: 'body-small',
      textContentClasses: 'tw-border tw-border-t-0',
    },
    nestedListingFilter: {
      buttonClasses: classNames(baseButtonClasses, 'tw-leading-sm', {
        'tw-px-md': padding,
      }),
    },
    tableItem: {
      buttonClasses: classNames(baseButtonClasses, 'tw-py-xs', {
        'tw-px-md': padding,
      }),
      buttonHeaderTextStyle: 'body-14-semibold',
      buttonContentTextStyle: 'body-small',
      textContentClasses: padding ? 'tw-px-md' : '',
    },
    noStyle: {
      buttonClasses: classNames(baseButtonClasses, 'tw-py-2.5'),
      buttonHeaderTextStyle: 'heading-21-light',
      buttonContentTextStyle: 'body',
      textContentClasses: '',
    },
    contentExpander: {
      buttonClasses: classNames(baseButtonClasses, 'tw-py-xs'),
      buttonHeaderTextStyle: 'headline',
      buttonContentTextStyle: 'body',
      textContentClasses:
        'tw-pb-md tw-pt-md md:tw-pb-xl xl:tw-pb-md md:tw-pt-lg xl:tw-pt-xl [&>section]:tw-p-0',
    },
    faqItem: {
      buttonClasses: classNames(baseButtonClasses, 'tw-py-3'),
      buttonHeaderTextStyle: 'body-18',
      buttonContentTextStyle: 'body',
      textContentClasses: 'tw-pb-6 tw-bg-white',
    },
    form: {
      buttonClasses: classNames(baseButtonClasses, 'tw-py-6 tw-border-t-1'),
      buttonHeaderTextStyle: 'body',
    },
    headline: {
      buttonClasses: classNames(
        baseButtonClasses,
        'tw-py-sm tw-text-grey-100 tw-border-t'
      ),
      buttonHeaderElement: (
        <>
          <Text variant="body" className="lg:tw-hidden">
            {title}
          </Text>
          <Text variant="body-18" className="tw-hidden lg:tw-block">
            {title}
          </Text>
        </>
      ),
    },
  };

  const baseTextClasses = classNames(
    'tw-text-grey-100 tw-border-grey-20',
    variantClasses[variant]?.textContentClasses
  );

  const handleClick = () => {
    setIsAccordionOpen(!isAccordionOpen);
    callback?.();
  };

  return (
    <div
      className={classNames({
        'tw-border-b tw-border-grey-20':
          variant !== 'filter' &&
          variant !== 'headline' &&
          variant !== 'nestedListingFilter',
      })}
    >
      <button
        type="button"
        aria-expanded={isAccordionOpen}
        className={classNames(
          baseAccordionButtonClasses,
          variantClasses[variant].buttonClasses,
          buttonClasses
        )}
        data-testid={dataTestId}
        onClick={handleClick}
      >
        {(!titleEl && variantClasses[variant].buttonHeaderElement) ?? (
          <div className="tw-w-full">
            <Text variant={variantClasses[variant].buttonHeaderTextStyle}>
              {title}
            </Text>
            {description && (
              <AnimatePresence mode="wait">
                {isAccordionOpen && (
                  <motion.div
                    className="tw-overflow-hidden"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                  >
                    <Text
                      variant="label-12"
                      as="p"
                      className="tw-pt-1 tw-text-grey-90 group-hover/button:tw-text-grey-70"
                    >
                      {description}
                    </Text>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        )}
        {!!titleEl && <div className="tw-flex">{titleEl}</div>}
        <div className="tw-flex tw-items-center tw-gap-2">
          {!!filterCount && (
            <span className="tw-ml-auto tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-grey-100 tw-font-semibold tw-text-white group-hover/button:tw-bg-grey-70">
              {filterCount}
            </span>
          )}
          <motion.div
            animate={{ rotate: isAccordionOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="tw-m-auto"
          >
            <Icon name="chevron-down" />
          </motion.div>
        </div>
      </button>

      <AnimatePresence mode="wait">
        {isAccordionOpen && (
          <motion.div
            initial={{ height: 0, overflow: 'hidden' }}
            animate={{
              height: 'auto',
              overflow: animateChildren ? 'visible' : 'hidden',
            }}
            exit={{ height: 0, overflow: 'hidden' }}
          >
            {children ? (
              <motion.div
                initial={{ opacity: animateChildren ? 0 : 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ ease: 'easeInOut', duration: 0.5, delay: 0.5 }}
                className={baseTextClasses}
              >
                {children}
              </motion.div>
            ) : (
              <Text
                as="p"
                variant={variantClasses[variant].buttonContentTextStyle}
                className={baseTextClasses}
              >
                {text}
              </Text>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccordionItem;
